// [TREATMENT]: `Node Pollutant R|C = expr` — the expression is a math
// formula, itself tokenized further by whitespace, so tokens 2+ are
// rejoined with single spaces before re-splitting on "=". This app has no
// water-quality routing engine yet; the expression is opaque text here,
// carried through unevaluated.

import { writeOrDeleteSection } from "./writeOrDeleteSection.ts";

export type TreatmentKind = "R" | "C";

export interface Treatment {
  node: string;
  pollutant: string;
  kind: TreatmentKind;
  expression: string;
}

export function readTreatment(raw: ReadonlyMap<string, string[][]>): Treatment[] {
  const rows = raw.get("TREATMENT") ?? [];
  const treatments: Treatment[] = [];
  for (const row of rows) {
    const [node, pollutant, ...rest] = row;
    if (!node || !pollutant || rest.length === 0) continue;
    const joined = rest.join(" ");
    const kind = joined[0]?.toUpperCase() === "C" ? "C" : "R";
    const equalsIndex = joined.indexOf("=");
    const expression = equalsIndex === -1 ? "" : joined.slice(equalsIndex + 1).trim();
    treatments.push({ node, pollutant, kind, expression });
  }
  return treatments;
}

export function writeTreatment(
  raw: ReadonlyMap<string, string[][]>,
  treatments: Treatment[]
): Map<string, string[][]> {
  // A blank expression still gets written as "0" rather than dropping the
  // row — every keystroke writes straight through with no local draft
  // state, so dropping the row here would delete it the instant a user
  // clears the field to type a replacement.
  const rows = treatments.map((t) => [
    t.node,
    t.pollutant,
    t.kind,
    "=",
    ...(t.expression.trim() || "0").split(/\s+/),
  ]);
  return writeOrDeleteSection(raw, "TREATMENT", rows);
}
